import {
  Alert,
  AlertIcon,
  Box,
  Button,
  HStack,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useToast,
} from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";

import useApi from "../../../shared/hooks/useApi";
import { components, ContractTypeId, OfficeId, ServiceCodeId } from "../../../shared/schema/schema";
import { ServiceCodeIssuesOverrideBody } from "../contract.mutations";
import Select from "../../../shared/components/Select";

type IssueTypeMeta = components["schemas"]["IssueTypeMeta"];
type ServiceCodeIssuesSetting = components["schemas"]["ServiceCodeIssuesSetting"];
type IssueGroup = "billing" | "payroll";

type Draft = Record<string, boolean | null>;

type Props = {
  contractTypeId: ContractTypeId;
  officeId: OfficeId;
};

const TRI_STATE_OPTIONS = [
  { value: "inherit", label: "Inherit" },
  { value: "on", label: "On" },
  { value: "off", label: "Off" },
] as const;

function fieldName(group: IssueGroup, key: string): string {
  return `${group}_issue_${key}`;
}

function valueToSelect(value: boolean | null): "inherit" | "on" | "off" {
  switch (value) {
    case true:
      return "on";
    case false:
      return "off";
    case null:
      return "inherit";
  }
}

function selectToValue(value: "inherit" | "on" | "off"): boolean | null {
  switch (value) {
    case "on":
      return true;
    case "off":
      return false;
    default:
      return null;
  }
}

// Build a draft holding every billing_issue_<key> / payroll_issue_<key> field,
// seeded from an existing override (or all-inherit).
function buildDraft(
  issueTypes: IssueTypeMeta[],
  override: ServiceCodeIssuesSetting | undefined
): Draft {
  const draft: Draft = {};
  for (const issueType of issueTypes) {
    for (const group of issueType.groups) {
      const name = fieldName(group, issueType.key);
      const existing = override as Record<string, boolean | null> | undefined;
      draft[name] = existing ? existing[name] ?? null : null;
    }
  }
  return draft;
}

export default function ServiceCodeIssuesOverride({ contractTypeId, officeId }: Props) {
  const { queries, mutations } = useApi();
  const queryClient = useQueryClient();
  const toast = useToast({ position: "top-right" });

  const settingsQueryOptions = queries.contract.serviceCodeIssuesSettings(contractTypeId, officeId);
  const settings = useQuery(settingsQueryOptions);

  const [selectedServiceCodeId, setSelectedServiceCodeId] = React.useState<ServiceCodeId | null>(
    null
  );
  const [draft, setDraft] = React.useState<Draft>({});

  const data = settings.data;

  const selectedOverride = React.useMemo(() => {
    if (data === undefined || selectedServiceCodeId === null) {
      return undefined;
    }
    return data.overrides.find((override) => override.serviceCodeId === selectedServiceCodeId);
  }, [data, selectedServiceCodeId]);

  // Default the selection to the first eligible service code once data loads.
  React.useEffect(() => {
    if (data !== undefined && selectedServiceCodeId === null && data.serviceCodes.length > 0) {
      setSelectedServiceCodeId(data.serviceCodes[0].id);
    }
  }, [data, selectedServiceCodeId]);

  // Reseed the draft whenever the selection changes or data is (re)loaded.
  React.useEffect(() => {
    if (data !== undefined) {
      setDraft(buildDraft(data.issueTypes, selectedOverride));
    }
  }, [data, selectedOverride]);

  const upsertMutation = useMutation({
    ...mutations.contract.upsertServiceCodeOverride(contractTypeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsQueryOptions.queryKey });
    },
    onError: () => {
      toast({ status: "error", title: "Failed to save service code overrides" });
    },
  });

  const deleteMutation = useMutation({
    ...mutations.contract.deleteServiceCodeOverride(contractTypeId),
    onSuccess: () => {
      toast({ status: "success", title: "Reset to inherit" });
      queryClient.invalidateQueries({ queryKey: settingsQueryOptions.queryKey });
    },
    onError: () => {
      toast({ status: "error", title: "Failed to reset overrides" });
    },
  });

  switch (settings.status) {
    case "error":
      return (
        <Alert status="error">
          <AlertIcon />
          Failed to load service code overrides
        </Alert>
      );
    case "pending":
      return (
        <HStack p={4}>
          <Spinner size="sm" />
          <Text>Loading service code overrides…</Text>
        </HStack>
      );
    case "success": {
      if (data === undefined || data.serviceCodes.length === 0) {
        return (
          <Box p={3}>
            <Text color="gray.500">
              No service codes are connected to this contract type for this office (via Direct Care
              Rates).
            </Text>
          </Box>
        );
      }

      // Auto-save: persist the override on every change (no explicit Save button).
      const persistDraft = (nextDraft: Draft) => {
        if (selectedServiceCodeId === null) {
          return;
        }
        const body = {
          officeId,
          serviceCodeId: selectedServiceCodeId,
          ...nextDraft,
        } as ServiceCodeIssuesOverrideBody;
        upsertMutation.mutate(body);
      };

      const handleIssueChange = (name: string, selectValue: "inherit" | "on" | "off") => {
        const nextDraft: Draft = { ...draft, [name]: selectToValue(selectValue) };
        setDraft(nextDraft);
        persistDraft(nextDraft);
      };

      const handleReset = () => {
        if (selectedServiceCodeId === null) {
          return;
        }
        if (selectedOverride !== undefined) {
          deleteMutation.mutate({ officeId, serviceCodeId: selectedServiceCodeId });
        } else {
          setDraft(buildDraft(data.issueTypes, undefined));
        }
      };

      const $serviceCodeSelectOptions = data.serviceCodes.map((serviceCode) => {
        const hasOverride = data.overrides.some(
          (override) => override.serviceCodeId === serviceCode.id
        );
        return {
          label: `${serviceCode.code}${hasOverride ? " (overridden)" : ""}`,
          value: serviceCode.id,
        } as const;
      });

      const $stateOptions = TRI_STATE_OPTIONS.map((option) => ({
        label: option.label,
        value: option.value,
      }));

      return (
        <Box>
          <HStack align="flex-end" justify="space-between" mb={3}>
            <Box maxW="320px">
              <Select
                label="Service Code"
                multiple={false}
                options={$serviceCodeSelectOptions}
                value={selectedServiceCodeId}
                onChange={(serviceCodeId) => setSelectedServiceCodeId(serviceCodeId ?? null)}
              />
            </Box>
            <HStack>
              {upsertMutation.isPending && (
                <HStack color="gray.500">
                  <Spinner size="xs" />
                  <Text fontSize="xs">Saving…</Text>
                </HStack>
              )}
              <Button
                isDisabled={selectedOverride === undefined}
                isLoading={deleteMutation.isPending}
                variant="outline"
                onClick={handleReset}
              >
                Reset to inherit
              </Button>
            </HStack>
          </HStack>

          <Text color="gray.500" fontSize="xs" mb={2}>
            Inherit uses the contract type setting for this office. On / Off overrides it for this
            service code. Changes are saved automatically.
          </Text>

          <TableContainer>
            <Table size="sm" variant="striped">
              <Thead>
                <Tr>
                  <Th>Issue</Th>
                  <Th>Billing</Th>
                  <Th>Payroll</Th>
                </Tr>
              </Thead>
              <Tbody>
                {data.issueTypes.map((issueType) => (
                  <Tr key={issueType.key}>
                    <Td>
                      <Tooltip hasArrow label={issueType.tooltip} placement="top">
                        <span>{issueType.label}</span>
                      </Tooltip>
                    </Td>
                    {(["billing", "payroll"] as IssueGroup[]).map((group) => {
                      const name = fieldName(group, issueType.key);
                      if (!issueType.groups.includes(group)) {
                        return (
                          <Td key={group} color="gray.300">
                            —
                          </Td>
                        );
                      }
                      return (
                        <Td key={group}>
                          <Select
                            allowUnselect={false}
                            label={`${group} ${issueType.label} issue setting`}
                            multiple={false}
                            options={$stateOptions}
                            value={valueToSelect(draft[name] ?? null)}
                            onChange={(val) => handleIssueChange(name, val ?? "inherit")}
                          />
                        </Td>
                      );
                    })}
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>
      );
    }
  }
}
